'use client'

import { isValidElement, useEffect, useMemo, useState } from 'react'
import { usePathname } from 'next/navigation'
import { Sidebar, getLeftPanelData } from '@/components/layout/Sidebar'
import { LeftPanel } from '@/components/layout/LeftPanel'
import { TopBar } from '@/components/layout/TopBar'
import { BottomBar } from '@/components/layout/BottomBar'
import { StoreSelector } from '@/components/layout/StoreSelector'
import { RequireAdmin } from '@/components/auth/RequireAdmin'
import { useUser } from '@/contexts/UserContext'
import { MobileNav } from '@/components/layout/MobileNav'
import '@/styles/access-denied.css'
import '@/styles/demo-onboarding-tour.css'

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://app.canerozen.com/api'

type AppChromeRouterProps = {
  children: React.ReactNode
}

type RouteGuardStatus = 'allowed' | 'checking' | 'switching' | 'denied'
type RewriteChromeStatus = 'idle' | 'checking' | 'bypass' | 'wrap'
type RewriteChromeState = {
  path: string
  status: Exclude<RewriteChromeStatus, 'checking'>
}

type RouteNavItem = {
  id?: number
  platform?: string | null
  url?: string | null
  path?: string | null
}

function normalizeRoutePath(value?: string | null) {
  if (!value) return ''
  const trimmed = value.trim().split('?')[0].split('#')[0]
  if (!trimmed || trimmed === '/') return trimmed
  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed
}

function routeItemMatchesPath(item: RouteNavItem, pathname: string) {
  const normalizedPathname = normalizeRoutePath(pathname)
  return [item.url, item.path].some(value => normalizeRoutePath(value) === normalizedPathname)
}

function isRouteGuardExcluded(pathname?: string | null) {
  if (!pathname) return true
  return (
    pathname === '/' ||
    pathname === '/login' ||
    pathname.startsWith('/login/') ||
    pathname === '/demo' ||
    pathname.startsWith('/demo/') ||
    pathname === '/market' ||
    pathname.startsWith('/market/') ||
    pathname === '/personal' ||
    pathname.startsWith('/personal/') ||
    pathname === '/embed' ||
    pathname.startsWith('/embed/') ||
    pathname === '/support' ||
    pathname.startsWith('/support/')
  )
}

function AccessDeniedPage() {
  return (
    <main className="access-denied-page">
      <a className="access-denied-back" href="/">
        <i className="fa-solid fa-chevron-left" aria-hidden="true" />
        Anasayfaya geri dön
      </a>
      <section className="access-denied-content" aria-labelledby="access-denied-title">
        <div className="access-denied-icon" aria-hidden="true">
          <i className="fa-solid fa-lock" />
        </div>
        <h1 id="access-denied-title">Erişiminiz Yok</h1>
        <p>Üzgünüz, bu sayfaya yetkiniz bulunmamaktadır.</p>
      </section>
    </main>
  )
}

function RouteGuardLoading() {
  return (
    <main className="access-denied-page access-denied-page--loading">
      <section className="access-denied-content" aria-live="polite">
        <div className="access-denied-icon" aria-hidden="true">
          <i className="fa-solid fa-circle-notch fa-spin" />
        </div>
        <h1>Yönlendiriliyor</h1>
        <p>Yetkili platform hazırlanıyor.</p>
      </section>
    </main>
  )
}

function isSingleSegmentPanelPath(pathname?: string | null) {
  if (!pathname || pathname === '/') return false
  const normalized = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
  return /^\/[^/]+$/.test(normalized)
}

const forceChromePaths = new Set([
  '/aktif-kampanyalar',
  '/hatali-toplamalar',
  '/tedarik-edilemeyenler',
  '/order-waitsuccess',
  '/bildirim-yonetimi',
  '/bilgi-bankasi',
  '/cerez-politikasi',
  '/diger',
  '/genel-ayarlar',
  '/gelen-giden-aramalar',
  '/hakkimizda',
  '/fatura-veri-besleme-kurallari',
  '/iptal-sebebleri',
  '/iade-sebebleri',
  '/kargo-veri-besleme-kurallari',
  '/kargo-yonetimi',
  '/lokasyon-yonetimi',
  '/musteri-bakiye-dokumu',
  '/odeme-secenekleri',
  '/personal',
  '/musteri-yorumlari',
  '/reklam-alanlari',
  '/remarketing',
  '/seo-yonetimi',
  '/sozlesme-prosedurler',
  '/tasarim',
  '/yardim-konulari',
])

const singleSegmentChromeBypassPaths = new Set([
  '/admin-system-department',
  '/admin-analiz-siparis-durumlari',
  '/alis-faturalari',
  '/alis-faturalari/yeni',
  '/cari-listesi',
  '/dahili-cikis',
  '/depot',
  '/depot-entry',
  '/depot-output',
  '/design-page',
  '/design-upload',
  '/dis-aramalar',
  '/embed',
  '/fatura-dokumu',
  '/fatura-tasarimlari',
  '/fiziki-kasa-ekrani',
  '/fiziki-kasalar',
  '/geri-odeme-listesi',
  '/gider-pusula-dokumu',
  '/kargo-desi-baremleri',
  '/kargo-etiket-tasarimlari',
  '/kargo-teslimat-dokumu',
  '/kdv-dokumu',
  '/kolay-odeme',
  '/kredi-karti-islemleri',
  '/logistic-invoice',
  '/logistic-invoice-op',
  '/logistic-list',
  '/logistic-missing',
  '/logistic-supply',
  '/maliyet-hesabi',
  '/market',
  '/musteri-gruplari',
  '/odeme-kontrol-talepleri',
  '/order',
  '/order-creditcard',
  '/order-list',
  '/order-notification',
  '/order-request',
  '/product',
  '/product-list',
  '/report',
  '/report-raporlar',
  '/return',
  '/sepet-tasima-araclari',
  '/sistem-analizi',
  '/sistem-analizi-siparis-durumlari',
  '/sistem-servisler',
  '/supply',
  '/taksit-oranlari',
  '/test-talepleri',
  '/ui',
  '/urun-nerede',
  '/urun-nerede-kontrol',
  '/urun-sevk-talebi',
])

function shouldBypassSingleSegmentChrome(pathname?: string | null) {
  const normalized = normalizeRoutePath(pathname)
  if (!normalized || forceChromePaths.has(normalized)) return false
  if (/^\/admin-library(?:-|$)/.test(normalized)) return true
  if (/^\/admin-system(?:-|$)/.test(normalized)) return true
  return singleSegmentChromeBypassPaths.has(normalized)
}

/**
 * Applies auth + panel chrome globally, excluding `/login`.
 *
 * Notes:
 * - `/pages/**` has its own nested layouts; we don't wrap those here.
 * - Many legacy pages already return a top-level `.panel-layout`; we avoid double-wrapping.
 *
 * Yeni sayfa açmadan önce not: src/app/pages/README.md
 */
export function AppChromeRouter({ children }: AppChromeRouterProps) {
  const pathname = usePathname()
  const { user, refreshUser } = useUser()

  const normalizedPathname = normalizeRoutePath(pathname)
  const routeGuardExcluded = useMemo(() => isRouteGuardExcluded(pathname), [pathname])
  const [routeGuardStatus, setRouteGuardStatus] = useState<RouteGuardStatus>('allowed')
  const [rewriteChromeState, setRewriteChromeState] = useState<RewriteChromeState>({ path: '', status: 'idle' })
  const shouldCheckRewriteChrome = Boolean(
    user &&
    normalizedPathname &&
    !routeGuardExcluded &&
    normalizedPathname !== '/pages' &&
    !normalizedPathname.startsWith('/pages/') &&
    isSingleSegmentPanelPath(normalizedPathname) &&
    !forceChromePaths.has(normalizedPathname)
  )
  const rewriteChromeStatus: RewriteChromeStatus = shouldCheckRewriteChrome
    ? (rewriteChromeState.path === normalizedPathname ? rewriteChromeState.status : 'checking')
    : 'idle'

  useEffect(() => {
    if (!user || routeGuardExcluded || !normalizedPathname) {
      setRouteGuardStatus('allowed')
      return
    }

    const currentUser = user
    if (Number(currentUser.admin || 0) === 1) {
      setRouteGuardStatus('allowed')
      return
    }

    let cancelled = false

    async function validateRouteAccess() {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        setRouteGuardStatus('allowed')
        return
      }

      try {
        setRouteGuardStatus('checking')
        const headers = { Authorization: `Bearer ${token}` }
        const [authorizedResponse, routeMapResponse] = await Promise.all([
          fetch(`${API_BASE}/auth/navs?platform=all`, {
            headers,
            credentials: 'include',
          }),
          fetch(`${API_BASE}/panel/nav-route-map`, {
            headers,
            credentials: 'include',
          }),
        ])

        if (cancelled) return
        if (!authorizedResponse.ok || !routeMapResponse.ok) {
          setRouteGuardStatus('allowed')
          return
        }

        const authorizedPayload = await authorizedResponse.json()
        const routeMapPayload = await routeMapResponse.json()
        const authorizedNavs = Array.isArray(authorizedPayload?.data?.navs) ? authorizedPayload.data.navs : []
        const routeMapNavs = Array.isArray(routeMapPayload?.data) ? routeMapPayload.data : []
        const authorizedMatch = authorizedNavs.find((item: RouteNavItem) => routeItemMatchesPath(item, normalizedPathname))
        const knownNavMatch = routeMapNavs.find((item: RouteNavItem) => routeItemMatchesPath(item, normalizedPathname))

        if (cancelled) return
        if (!knownNavMatch) {
          setRouteGuardStatus('allowed')
          return
        }

        if (!authorizedMatch) {
          setRouteGuardStatus('denied')
          return
        }

        const targetPlatform = String(authorizedMatch.platform || '').trim()
        if (targetPlatform && targetPlatform !== currentUser.role) {
          setRouteGuardStatus('switching')
          const switchResponse = await fetch(`${API_BASE}/auth/change-platform`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            credentials: 'include',
            body: JSON.stringify({
              platform: targetPlatform,
              store_scope: localStorage.getItem('admin_store_nav_scope') === '1',
            }),
          })

          if (cancelled) return
          if (!switchResponse.ok) {
            setRouteGuardStatus('denied')
            return
          }

          await refreshUser()
          if (!cancelled) setRouteGuardStatus('allowed')
          return
        }

        setRouteGuardStatus('allowed')
      } catch (error) {
        console.warn('[AppChromeRouter] route access guard skipped:', error)
        if (!cancelled) setRouteGuardStatus('allowed')
      }
    }

    validateRouteAccess()

    return () => {
      cancelled = true
    }
  }, [normalizedPathname, routeGuardExcluded, user?.admin, user?.id, user?.role])

  useEffect(() => {
    if (!shouldCheckRewriteChrome) return

    let cancelled = false

    async function resolveRewriteChrome() {
      try {
        const response = await fetch(`${API_BASE}/panel/nav-route-map`, {
          headers: { Accept: 'application/json' },
          credentials: 'include',
        })
        if (cancelled) return
        if (!response.ok) {
          setRewriteChromeState({ path: normalizedPathname, status: 'wrap' })
          return
        }

        const payload = await response.json().catch(() => null)
        const navs = Array.isArray(payload?.data) ? payload.data : []
        const match = navs.find((item: RouteNavItem) => (
          normalizeRoutePath(item.url) === normalizedPathname &&
          normalizeRoutePath(item.path).startsWith('/pages/')
        ))
        setRewriteChromeState({ path: normalizedPathname, status: match ? 'bypass' : 'wrap' })
      } catch (error) {
        console.warn('[AppChromeRouter] rewrite chrome check skipped:', error)
        if (!cancelled) setRewriteChromeState({ path: normalizedPathname, status: 'wrap' })
      }
    }

    resolveRewriteChrome()

    return () => {
      cancelled = true
    }
  }, [normalizedPathname, shouldCheckRewriteChrome])

  if (routeGuardStatus === 'denied') {
    return <AccessDeniedPage />
  }

  if (routeGuardStatus === 'checking' || routeGuardStatus === 'switching') {
    return <RouteGuardLoading />
  }

  if (rewriteChromeStatus === 'checking') {
    return <RouteGuardLoading />
  }

  const navSlugRoute = user?.nav?.find(item => {
    if (!item.url) return false
    const navUrl = normalizeRoutePath(item.url)
    return navUrl === normalizedPathname
  })
  const isNavSlugRoute = Boolean(navSlugRoute)
  const isNavSlugRewriteToPages = Boolean(navSlugRoute?.path && normalizeRoutePath(navSlugRoute.path).startsWith('/pages/'))
  const childProps = isValidElement<{ className?: string }>(children) ? children.props : null
  const childIsPanelNotFound =
    typeof childProps?.className === 'string' &&
    childProps.className.includes('panel-not-found-route')

  if (pathname === '/login' || pathname?.startsWith('/login/')) {
    return children
  }

  if (pathname === '/demo' || pathname?.startsWith('/demo/')) {
    return children
  }

  // Root dashboard manages its own panel chrome (page.tsx)
  if (pathname === '/') {
    return children
  }

  if (pathname === '/pages' || pathname?.startsWith('/pages/')) {
    return children
  }

  if (rewriteChromeStatus === 'bypass') {
    return children
  }

  // Public system_nav URLs such as `/admin-firm-list` are rewritten by middleware
  // to `/pages/**`. The nested `/pages/layout.tsx` owns the panel chrome there;
  // wrapping here as well creates duplicated TopBar/Sidebar layers.
  if (isNavSlugRewriteToPages && !forceChromePaths.has(normalizedPathname) && !childIsPanelNotFound) {
    return children
  }

  if (pathname?.startsWith('/personal/')) {
    return children
  }

  // Middleware rewrites this public nav URL to `/pages/product/list/`.
  // The rewritten page is already wrapped by `app/pages/layout.tsx`; wrapping here too
  // intermittently creates duplicate TopBar/Sidebar layers on hard refresh.
  if (pathname === '/product-list' || pathname?.startsWith('/product-list/')) {
    return children
  }

  if (pathname === '/cari-listesi' || pathname?.startsWith('/cari-listesi/')) {
    return children
  }

  // Direct system_nav slugs are rewritten to `/pages/**` by middleware.
  // Let the nested `/pages/layout.tsx` own the panel chrome to avoid double sidebars.
  if (isNavSlugRoute && shouldBypassSingleSegmentChrome(normalizedPathname) && !childIsPanelNotFound) {
    return children
  }

  if (pathname === '/embed' || pathname?.startsWith('/embed/')) {
    return children
  }

  // Dedicated support portal should not render panel chrome
  if (pathname === '/support' || pathname?.startsWith('/support/')) {
    return children
  }

  // These pages render their own panel chrome
  if (
    pathname === '/market' ||
    pathname?.startsWith('/sistem-analizi') ||
    pathname?.startsWith('/Library/')
  ) {
    return children
  }

  // Public system_nav slugs are rewritten by middleware to `/pages/**`, where
  // `app/pages/layout.tsx` already owns the panel chrome. Do not depend on the
  // currently selected platform's nav here: another browser tab can change that
  // platform and make a still-open rewritten page look like a non-nav route.
  if (isSingleSegmentPanelPath(pathname) && shouldBypassSingleSegmentChrome(normalizedPathname) && !childIsPanelNotFound) {
    return children
  }

  // Public nav pages rewritten to `/pages/**`; `/pages/layout.tsx` already owns chrome.
  // Detail pages like `/alis-faturalari/1` are real app routes and need this chrome.
  if (pathname === '/alis-faturalari' || pathname === '/alis-faturalari/yeni') {
    return children
  }

  const childIsLegacyPanelLayout =
    typeof childProps?.className === 'string' &&
    childProps.className.includes('panel-layout')

  return (
    <RequireAdmin requireAdmin={false}>
      {childIsLegacyPanelLayout ? (
        children
      ) : user ? (
        <div className="panel-layout">
          <Sidebar
            collapsed={pathname !== '/'}
            nav={user.nav || []}
            platforms={user.platforms || []}
            currentPlatform={user.role}
          />

          {(() => {
            const leftPanelData = getLeftPanelData(pathname, user.nav || [])
            const parentMenu = leftPanelData?.parentMenu
            const subMenus = leftPanelData?.subMenus || []
            const activeMenuIds = leftPanelData?.activeMenuIds || []

            if (!parentMenu || subMenus.length === 0) return null

            return (
              <LeftPanel title={parentMenu.name} icon={parentMenu.icon} items={subMenus} activeIds={activeMenuIds} />
            )
          })()}

          <div className="panel-main">
            <TopBar dashboardSelector={<StoreSelector />} />
            <main className="panel-content">{children}</main>
            <BottomBar />
            <MobileNav />
          </div>
        </div>
      ) : null}
    </RequireAdmin>
  )
}
